import React, { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { resolveMediaUrl } from '../constants/config';
import { useChampionships } from '../utilities/championships';
import { adminSearch, searchTypeLabelKey } from '../utilities/adminSearch';
import { useI18n } from '../utilities/i18n';
import { getApiErrorMessage } from '../utilities/stadiumsApi';

export default function AdminSearchPicker({
  type,
  placeholder,
  disabled,
  onPick,
  scopeChamp = false,
  showType = false,
  excludeKeys = [],
}) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId } = useChampionships();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setQuery('');
    setItems([]);
    setError('');
    setOpen(false);
  }, [type]);

  useEffect(() => {
    if (!open || disabled || !type) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      loadItems(query);
    }, query ? 250 : 0);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, open, disabled, type, scopeChamp, champId]);

  const loadItems = async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const page = await adminSearch(dispatch, {
        type,
        search,
        champId: scopeChamp ? champId : undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setItems(page.items);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setItems([]);
      setError(getApiErrorMessage(err, t('searchLoadError')));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const visibleItems = items.filter((item) => {
    const itemType = item.type || type;
    return !excludeKeys.includes(`${itemType}:${item.id}`);
  });
  const withType = showType || type === 'all';

  if (!type) return null;

  return (
    <div className="admin-search-picker" ref={wrapRef}>
      <div className="st-search">
        <FiSearch className="st-search-icon" />
        <input
          type="text"
          className="st-search-input"
          placeholder={placeholder || t('searchEntityPh')}
          value={query}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setError('');
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              if (visibleItems[0]) {
                onPick(visibleItems[0]);
                setQuery('');
                setOpen(false);
              }
            }
            if (event.key === 'Escape') setOpen(false);
          }}
        />
        {query ? (
          <button type="button" className="st-search-clear" onClick={() => setQuery('')}>
            ×
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div className="news-tag-dropdown">
          {loading ? <div className="news-tag-empty">{t('searchLoading')}</div> : null}
          {!loading && visibleItems.map((item) => {
            const itemType = item.type || type;
            const imageUrl = itemType !== 'city' ? resolveMediaUrl(item.image) : '';
            const typeKey = searchTypeLabelKey(itemType);
            return (
              <button
                key={`${itemType}-${item.id}`}
                type="button"
                className="news-tag-option admin-search-option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onPick(item);
                  setQuery('');
                  setOpen(false);
                }}
              >
                {imageUrl ? <img className="admin-search-option-image" src={imageUrl} alt="" /> : null}
                {withType && typeKey ? <span className="admin-search-option-type">{t(typeKey)}</span> : null}
                <span className={`admin-search-option-text${item.subtitle ? ' has-subtitle' : ''}`}>
                  {item.subtitle ? <small>{item.subtitle}</small> : null}
                  <span className="admin-search-option-title">{item.name}</span>
                </span>
              </button>
            );
          })}
          {!loading && !visibleItems.length ? (
            <div className="news-tag-empty">{error || t('searchEmpty')}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
